/**
 * Network Visualization Component
 * Visual graph showing Sponsor → Programs → CBOs relationships
 * Trust Marketplace - Phase 4: Network Management System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { CapabilityGate } from '../common/CapabilityGate';
import { getActiveCboId } from '@/services/api/organizations';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Building2,
  Target,
  Network as NetworkIcon,
  Users,
  TrendingUp,
  MapPin,
  DollarSign,
  Award,
  ChevronRight,
  ChevronDown,
  Eye,
  Filter
} from 'lucide-react';

interface NetworkVisualizationProps {
  programId?: number;
  sponsorId?: number;
}

interface NetworkNode {
  sponsor_id: number;
  sponsor_name: string;
  sponsor_type?: string;
  total_programs: number;
  total_cbos: number;
  total_members: number;
  total_funding: number;
  programs: Array<{
    program_id: number;
    program_name: string;
    program_type?: string;
    participating_cbos: number;
    total_enrollments: number;
    incentives_distributed: number;
    cbos: Array<{
      cbo_id: number;
      cbo_name: string;
      service_area?: string;
      enrolled_members: number;
      incentives_earned: number;
      performance_score?: number;
      participation_status: string;
    }>;
  }>;
}

interface NetworkOverview {
  total_sponsors: number;
  total_programs: number;
  total_cbos: number;
  total_members: number;
  total_funding: number;
  network_nodes: NetworkNode[];
}

const NetworkVisualizationComponent: React.FC<NetworkVisualizationProps> = ({ programId, sponsorId }) => {
  const { t } = useAppTranslation();
  const [networkData, setNetworkData] = useState<NetworkOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedState, setSelectedState] = useState<string>('');
  const [expandedSponsors, setExpandedSponsors] = useState<Set<number>>(new Set());
  const [expandedPrograms, setExpandedPrograms] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchNetworkData();
  }, [programId, sponsorId, selectedState]);

  const fetchNetworkData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // Build URL based on filters
      let url = '/api/network-overview';
      const params = new URLSearchParams();
      if (programId) params.append('program_id', programId.toString());
      if (sponsorId) params.append('sponsor_id', sponsorId.toString());
      if (selectedState) params.append('state', selectedState);

      const fullUrl = getWeaveinApiUrl(`${url}?${params.toString()}`);
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || ''
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch network data: ${response.status}`);
      }

      const data = await response.json();
      setNetworkData(data);

      // Auto-expand if single sponsor
      if (data.network_nodes && data.network_nodes.length === 1) {
        setExpandedSponsors(new Set([data.network_nodes[0].sponsor_id]));
      }
    } catch (err) {
      console.error('Error fetching network data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleSponsor = (sponsorId: number) => {
    const newExpanded = new Set(expandedSponsors);
    if (newExpanded.has(sponsorId)) {
      newExpanded.delete(sponsorId);
    } else {
      newExpanded.add(sponsorId);
    }
    setExpandedSponsors(newExpanded);
  };

  const toggleProgram = (programId: number) => {
    const newExpanded = new Set(expandedPrograms);
    if (newExpanded.has(programId)) {
      newExpanded.delete(programId);
    } else {
      newExpanded.add(programId);
    }
    setExpandedPrograms(newExpanded);
  };

  const expandAll = () => {
    if (!networkData || !networkData.network_nodes) return;
    const allSponsors = new Set(networkData.network_nodes.map(n => n.sponsor_id));
    const allPrograms = new Set(
      networkData.network_nodes.flatMap(n => (n.programs || []).map(p => p.program_id))
    );
    setExpandedSponsors(allSponsors);
    setExpandedPrograms(allPrograms);
  };

  const collapseAll = () => {
    setExpandedSponsors(new Set());
    setExpandedPrograms(new Set());
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(amount);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  if (!networkData) {
    return (
      <Card className="p-12 text-center">
        <NetworkIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('network.no_network_data_available', 'No network data available')}</h3>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <NetworkIcon className="w-7 h-7 text-blue-600" />
            {t('network.network_overview', 'Network Overview')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('network.sponsor_program_cbo_relationships', 'Sponsor → Program → CBO relationships')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            <ChevronDown className="w-4 h-4 mr-1" />
            {t('network.expand_all', 'Expand All')}
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            <ChevronRight className="w-4 h-4 mr-1" />
            {t('network.collapse_all', 'Collapse All')}
          </Button>
        </div>
      </div>

      {/* Network Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('network.sponsors', 'Sponsors')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{networkData.total_sponsors}</div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{t('network.programs', 'Programs')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">{networkData.total_programs}</div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('network.cbos', 'CBOs')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{networkData.total_cbos}</div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-yellow-700 font-medium">{t('network.members', 'Members')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">
            {(networkData.total_members || 0).toLocaleString()}
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-indigo-600" />
            <span className="text-xs text-indigo-700 font-medium">{t('network.funding', 'Funding')}</span>
          </div>
          <div className="text-2xl font-bold text-indigo-900">
            {formatCurrency(networkData.total_funding || 0)}
          </div>
        </Card>
      </div>

      {/* Network Hierarchy */}
      <div className="space-y-4">
        {(networkData.network_nodes || []).map((sponsor) => {
          const isSponsorExpanded = expandedSponsors.has(sponsor.sponsor_id);

          return (
            <Card key={sponsor.sponsor_id} className="overflow-hidden">
              {/* Sponsor Node */}
              <div
                className="p-5 bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200 cursor-pointer hover:from-blue-100 hover:to-blue-150 transition-colors"
                onClick={() => toggleSponsor(sponsor.sponsor_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">{sponsor.sponsor_name}</h3>
                        {sponsor.sponsor_type && (
                          <Badge variant="outline" className="text-xs">
                            {sponsor.sponsor_type}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {sponsor.total_programs} programs
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {sponsor.total_cbos} CBOs
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {(sponsor.total_members || 0).toLocaleString()} members
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(sponsor.total_funding || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {isSponsorExpanded ? (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-blue-600" />
                  )}
                </div>
              </div>

              {/* Programs */}
              {isSponsorExpanded && (
                <div className="p-5 space-y-3">
                  {(sponsor.programs || []).map((program, programIndex) => {
                    const isProgramExpanded = expandedPrograms.has(program.program_id);

                    return (
                      <div key={program.program_id} className="ml-8">
                        {/* Connector Line */}
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-px bg-gradient-to-r from-blue-300 to-purple-300"></div>
                          <div className="flex-1">
                            <Card
                              className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => toggleProgram(program.program_id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 text-white">
                                    <Target className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-gray-900">{program.program_name}</h4>
                                      {program.program_type && (
                                        <Badge variant="outline" className="text-xs">
                                          {program.program_type}
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                      <span className="flex items-center gap-1">
                                        <Building2 className="w-3 h-3" />
                                        {program.participating_cbos} CBOs
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        {program.total_enrollments} enrolled
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Award className="w-3 h-3" />
                                        {formatCurrency(program.incentives_distributed)} distributed
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {isProgramExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-purple-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-purple-600" />
                                )}
                              </div>
                            </Card>
                          </div>
                        </div>

                        {/* CBOs */}
                        {isProgramExpanded && (
                          <div className="ml-12 space-y-2">
                            {(program.cbos || []).map((cbo) => (
                              <div key={cbo.cbo_id} className="flex items-center">
                                <div className="w-8 h-px bg-gradient-to-r from-purple-300 to-green-300"></div>
                                <div className="flex-1">
                                  <Card className="p-3 bg-gradient-to-r from-green-50 to-green-100 border-green-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3 flex-1">
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white">
                                          <Building2 className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <h5 className="font-medium text-gray-900 text-sm">{cbo.cbo_name}</h5>
                                            <Badge className={`text-xs ${getStatusColor(cbo.participation_status)}`}>
                                              {cbo.participation_status}
                                            </Badge>
                                          </div>
                                          <div className="flex items-center gap-3 text-xs text-gray-600">
                                            {cbo.service_area && (
                                              <span className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {cbo.service_area}
                                              </span>
                                            )}
                                            <span className="flex items-center gap-1">
                                              <Users className="w-3 h-3" />
                                              {cbo.enrolled_members} enrolled
                                            </span>
                                            <span className="flex items-center gap-1 text-green-600 font-medium">
                                              <DollarSign className="w-3 h-3" />
                                              {formatCurrency(cbo.incentives_earned)}
                                            </span>
                                            {cbo.performance_score !== undefined && (
                                              <span className="flex items-center gap-1 text-blue-600 font-medium">
                                                <TrendingUp className="w-3 h-3" />
                                                {cbo.performance_score}% score
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          window.location.href = `/organizations/cbo/${cbo.cbo_id}`;
                                        }}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </Card>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Legend */}
      <Card className="p-4 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span className="text-xs text-gray-600">{t('network.sponsor', 'Sponsor')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-600"></div>
              <span className="text-xs text-gray-600">{t('network.program', 'Program')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-600"></div>
              <span className="text-xs text-gray-600">CBO</span>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {t('network.click_on_nodes_to_expandcollapse', 'Click on nodes to expand/collapse relationships')}
          </div>
        </div>
      </Card>
    </div>
  );
};

// Wrap with capability gate
const NetworkVisualization: React.FC<NetworkVisualizationProps> = (props) => {
  const cboId = getActiveCboId();

  return (
    <CapabilityGate
      organizationId={cboId}
      capability="network_directory"
      behavior="show-upgrade"
      upgradeMessage="Network Directory & Visualization is not enabled for your organization. Contact your administrator to enable this premium feature."
    >
      <NetworkVisualizationComponent {...props} />
    </CapabilityGate>
  );
};

export default NetworkVisualization;
